import React, { useState, useEffect, useRef, useMemo } from 'react'
import {useParams, useSearchParams} from 'react-router-dom';
import './searchObject.scss'
import AIPanel from "../../../components/AIChat/AIPanel";
import { useConvokeDomainContext } from '../ConvokeDomainProvider';
import {TableControl} from 'spark-core-dx/components';
import {useCoreRoute} from 'spark-core-dx/hooks'
import { useCoreContext, useCoreStateContext } from 'spark-core-dx/contexts';
import ConvokeEntityObjectManager from '../../../managers/convoke/ConvokeEntityObjectManager';

const getPanelText = (value) => String(value ?? "").trim();

const parsePanelParams = (value) => {
    if (!value) {
        return {};
    }
    if (typeof value === "object" && !Array.isArray(value)) {
        return value;
    }
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    }
    return {};
};

const readFirstPanelParam = (params, keys) => {
    for (const key of keys) {
        const value = getPanelText(params?.[key]);
        if (value) {
            return value;
        }
    }
    return "";
};

const getObjectPropCI = (obj, ...names) => {
    if (!obj || typeof obj !== "object") {
        return undefined;
    }
    const lookup = new Map(Object.keys(obj).map((key) => [String(key).toLowerCase(), key]));
    for (const name of names) {
        const key = lookup.get(String(name).toLowerCase());
        if (key !== undefined) {
            return obj[key];
        }
    }
    return undefined;
};

const parseMaybeJsonValue = (value) => {
    if (typeof value !== "string") {
        return value;
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return value;
    }
    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
};

const looksLikeFilterQuery = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }
    return getObjectPropCI(
        value,
        "EntityObjectId",
        "entityObjectId",
        "RootFilterNode",
        "rootFilterNode",
        "SelectColumns",
        "selectColumns",
        "Skip",
        "skip",
        "Take",
        "take",
        "SortBy",
        "sortBy"
    ) !== undefined;
};

const resolveFilterQueryPayload = (payload, seen = new Set()) => {
    const parsed = parseMaybeJsonValue(payload);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return null;
    }
    if (seen.has(parsed)) {
        return null;
    }
    seen.add(parsed);

    if (looksLikeFilterQuery(parsed)) {
        return parsed;
    }

    const args = getObjectPropCI(parsed, "args", "Args");
    const metadata = getObjectPropCI(parsed, "metadata", "Metadata");
    const candidates = [
        getObjectPropCI(metadata, "query", "Query"),
        getObjectPropCI(args, "query", "Query"),
        getObjectPropCI(args, "output", "Output"),
        getObjectPropCI(parsed, "query", "Query"),
        getObjectPropCI(parsed, "generatedQuery", "GeneratedQuery"),
        getObjectPropCI(parsed, "value", "Value"),
        getObjectPropCI(parsed, "output", "Output")
    ];

    for (const candidate of candidates) {
        const resolved = resolveFilterQueryPayload(candidate, seen);
        if (resolved) {
            return resolved;
        }
    }

    return null;
};

const resolvePanelFilterQuery = (item, value, entityObjectId) => {
    const candidates = [
        value,
        item?.GeneratedQuery,
        item?.generatedQuery,
        item?.Value,
        item?.value,
        item?.Output,
        item?.output,
        item
    ];

    for (const candidate of candidates) {
        const resolved = resolveFilterQueryPayload(candidate);
        if (resolved) {
            const existingId = getObjectPropCI(resolved, "EntityObjectId", "entityObjectId");
            return {
                ...resolved,
                EntityObjectId: existingId || entityObjectId
            };
        }
    }

    return null;
};

const firstMetadataItem = (metadata) => {
    if (Array.isArray(metadata)) {
        return metadata.first?.() ?? metadata[0];
    }
    return metadata;
};

const toTextList = (value) => {
    if (Array.isArray(value)) {
        return value.map((entry) => String(entry ?? "").trim()).filter(Boolean);
    }
    return [];
};

const SearchEntityObject = ({
    panelMode = false,
    panelOpen = true,
    panelId = null,
    panelReferenceId = null,
    panelSessionId = null,
    panelParams = null,
    onPanelClose = null
}) => {

    const [data, setData] = useState(null);
    const [fieldOverrides, setFieldOverrides] = useState(null);
    const {coreNavigate} = useCoreRoute();
    const domainContext = useConvokeDomainContext() ?? {};
    const coreContext = useCoreContext();
    const coreState = useCoreStateContext();
    const {eoId: routeEoId} = useParams();
     const [searchParams, setSearchParams] = useSearchParams();
    const resolvedPanelParams = useMemo(() => parsePanelParams(panelParams), [panelParams]);
    const eoId = panelMode
        ? (getPanelText(panelId) || getPanelText(searchParams.get("aiPanelId")) || routeEoId)
        : routeEoId;
    const wId = readFirstPanelParam(resolvedPanelParams, ["wId", "wid", "warehouseId", "WarehouseId"])
        || getPanelText(searchParams.get('wId'));
    const wdId = readFirstPanelParam(resolvedPanelParams, ["wdId", "wdid", "warehouseDeploymentId", "WarehouseDeploymentId"])
        || getPanelText(panelReferenceId)
        || getPanelText(searchParams.get(panelMode ? "aiPanelReferenceId" : 'wdId'));
    const panelSid = getPanelText(panelSessionId) || getPanelText(searchParams.get("aiPanelSessionId"));
    const profileReady = Boolean(coreContext?.Profile?.ClientId && coreContext?.Profile?.AppId);
    const shouldUseDomainEntityObjectMgr = !panelMode && domainContext.entityObjectMgr;
    const fallbackEntityObjectMgr = useMemo(() => {
        if (shouldUseDomainEntityObjectMgr || !eoId || !profileReady) {
            return null;
        }
        return new ConvokeEntityObjectManager(eoId, coreContext, coreState, "SearchEntityObject", true);
    }, [coreContext, coreState, eoId, profileReady, shouldUseDomainEntityObjectMgr]);
    const entityObjectMgr = shouldUseDomainEntityObjectMgr ? domainContext.entityObjectMgr : fallbackEntityObjectMgr;
    const loadingState = domainContext.loadingState ?? entityObjectMgr?.loading?.All ?? false;
    const [initialSearch,setInitialSearch] = useState(false);
    const pageRef = useRef(null);
    const tableData = data ?? entityObjectMgr?.filteredData ?? [];
    const selectedChatSession = useMemo(
        () => panelMode && panelSid ? { _id: panelSid } : null,
        [panelMode, panelSid]
    );
    const tableMetadata = useMemo(() => {
        const metadataList = entityObjectMgr?.uiMetadata;
        if (!Array.isArray(metadataList) || metadataList.length === 0) {
            return metadataList;
        }

        const deepCopy = (value) => typeof structuredClone === "function" ? structuredClone(value) : JSON.parse(JSON.stringify(value));
        return metadataList.map((metadataItem) => {
            const nextMetadata = deepCopy(metadataItem);
            const fields = Array.isArray(nextMetadata?.Fields) ? nextMetadata.Fields : [];
            const hasPaging = Array.isArray(nextMetadata?.HasPaging) && nextMetadata.HasPaging.length > 0;

            if (nextMetadata?.ServerFilterSort && !hasPaging) {
                nextMetadata.ServerFilterSort = false;
            }

            const hasVisibleColumns = fields.some(field => field?.ShowOnGrid && !field?.SubUIItemId);
            if (!hasVisibleColumns && fields.length > 0) {
                const preferredNames = new Set(['name', 'displayname', 'display_name', 'partner_name', 'contact_name', 'email_from', 'phone', 'website']);
                let selectedFields = fields.filter(field => field?.PrimaryField || preferredNames.has((field?.FieldName ?? '').toLowerCase()));
                if (selectedFields.length === 0) {
                    selectedFields = fields.filter(field => !field?.SubUIItemId).slice(0, 8);
                }

                const selectedFieldNames = new Set(selectedFields.map(field => field.FieldName));
                nextMetadata.Fields = fields.map((field) => {
                    if (field?.SubUIItemId) {
                        return field;
                    }

                    return {
                        ...field,
                        ShowOnGrid: selectedFieldNames.has(field.FieldName)
                    };
                });
            }

            return nextMetadata;
        });
    }, [entityObjectMgr?.uiMetadata]);
       

    useEffect(() => {
        if (!entityObjectMgr || !eoId || !profileReady) {
            return;
        }

        if (!data && tableMetadata && !initialSearch) {
            setInitialSearch(true);
            const metadata = tableMetadata?.length === undefined ? tableMetadata : tableMetadata?.first();
            let take = metadata?.ProfilePreferences?.HasPaging ? metadata?.ProfilePreferences?.HasPaging.first() : metadata?.HasPaging ? metadata?.HasPaging.first() : 50000;
            const isServerSide = metadata?.ProfilePreferences?.ServerFilterSort !== undefined && metadata?.ProfilePreferences?.ServerFilterSort !== null   ? 
                                 metadata?.ProfilePreferences?.ServerFilterSort : metadata?.ServerFilterSort;
            const initialQueryParams = { search: true, ...(wId ? { wId } : {}), ...(wdId ? { wdId } : {}) };
            entityObjectMgr?.search({
                id: eoId,
                queryParams: initialQueryParams,
                forceUpdate: true,
                take: (isServerSide ? take : 50000),
            })
        }
        else if(entityObjectMgr && !entityObjectMgr?.uiMetadata && typeof entityObjectMgr?.LoadMetadata === "function") {
            entityObjectMgr.LoadMetadata();
        }
    },[data, eoId, entityObjectMgr, tableMetadata, entityObjectMgr?.uiMetadata, initialSearch, profileReady, wId, wdId])

    useEffect(() => {
        
        if (!loadingState && entityObjectMgr) {
            
            setData(entityObjectMgr?.filteredData)
        }
    }, [loadingState, entityObjectMgr?.filteredData])

    
    const onServerSideQuery = async (itemQuery) => {
        if(!wId || !wdId)
            return await entityObjectMgr.search({id:eoId,queryParams:{search:true}, searchModel: itemQuery, forceUpdate: true })
        else
            return await entityObjectMgr.search({id:eoId,queryParams:{wId,wdId,search:true}, searchModel: itemQuery, forceUpdate: true })
    }

    const onSessionChange = (sessionId) => {
        if (!panelMode) {
            return;
        }

        const nextParams = new URLSearchParams(searchParams);
        const normalizedSessionId = getPanelText(sessionId);
        if (normalizedSessionId) {
            nextParams.set("aiPanelSessionId", normalizedSessionId);
        } else {
            nextParams.delete("aiPanelSessionId");
        }
        setSearchParams(nextParams);
    }

    const onUpdatePanel = async (UICommand, item, value)=>{
        if(UICommand === "filter"){
              const filterQuery = resolvePanelFilterQuery(item, value, eoId);
              if (!filterQuery) return;

              const selectColumns = toTextList(getObjectPropCI(filterQuery, "SelectColumns", "selectColumns"))
              const deepCopy = v => typeof structuredClone === "function" ? structuredClone(v) : JSON.parse(JSON.stringify(v)); // fallback
              const metadataItem = firstMetadataItem(entityObjectMgr?.uiMetadata);
              const metadataFields = Array.isArray(metadataItem?.Fields) ? metadataItem.Fields : [];
                if (selectColumns && selectColumns.length > 0 && metadataFields.length > 0) {
                    const allowed = new Set(selectColumns.map(s => s.toLowerCase()));
                    let fieldToOverride = metadataFields.map(deepCopy);
                    // Flip ShowOnGrid on the copies only
                    fieldToOverride = fieldToOverride.map(f => {
                        const name = (f?.FieldName ?? "").toString().toLowerCase();
                        if (!allowed.has(name)) {
                            f.ShowOnGrid = false;
                            f.IsActive = false;
                            f.IsDeleted = true;
                        }
                        return f;
                    });

                    setFieldOverrides(fieldToOverride);
                }

            const wid = getObjectPropCI(filterQuery, "warehouseId", "WarehouseId", "wid", "wId") ?? getObjectPropCI(value, "warehouseId", "WarehouseId", "wid", "wId") ?? getObjectPropCI(item, "warehouseId", "WarehouseId", "wid", "wId") ?? wId;
            const wdid = getObjectPropCI(filterQuery, "warehouseDeploymentId", "WarehouseDeploymentId", "wdid", "wdId") ?? getObjectPropCI(value, "warehouseDeploymentId", "WarehouseDeploymentId", "wdid", "wdId") ?? getObjectPropCI(item, "warehouseDeploymentId", "WarehouseDeploymentId", "wdid", "wdId") ?? wdId;
            const queryParams = { search: true, ...(wid ? { wid } : {}), ...(wdid ? { wdid } : {}) };

            const filterEntityObjectId = getObjectPropCI(filterQuery, "EntityObjectId", "entityObjectId") ?? eoId;
            const result = await entityObjectMgr.search({id:filterEntityObjectId, searchModel: filterQuery, queryParams, forceUpdate: true })
            if (Array.isArray(result?.Items)) {
                setData(result.Items);
            }
        }
    }


    return ( <>
                    {entityObjectMgr?.uiMetadata?.AISupportsSession || true ?
            		<AIPanel
						childrenRef={pageRef}
						open={panelOpen}
                        presentation={panelMode ? "slideout" : "inline"}
						onUpdatePanel={onUpdatePanel}
                        onSessionChange={panelMode ? onSessionChange : undefined}
                        session={selectedChatSession}
						mapToClass={'core-layout'}
                        params={{wdId,wId}}
						referenceId={eoId}
                        referenceName={"object"}
                        operationName = {"AIQuestionEntityObject"}
                        directModeOnly={true}
						onCancel={()=>{
                            if (panelMode && typeof onPanelClose === "function") {
                                onPanelClose();
                            } else {
                                coreNavigate("/dynamic/entityobject/");
                            }
                        }}
                        >
               <div className="ai-panel__table">
                            <TableControl
                                data={tableData}
                                className={"search-object-table"}
                                enableMultiSelect={false}
                                defaultNumRows={50}
                                onServerSideQuery={onServerSideQuery}
                                metadata={tableMetadata}
                                loadingState={loadingState}
                                filters={entityObjectMgr?.filters}
                                mgr={entityObjectMgr}
                                fieldOverrides = {fieldOverrides}
                                enableSidePanelEdit={true}
                            />
                            </div>
					</AIPanel> :
                     <TableControl
                                data={tableData}
                                className={"search-object-table"}
                                enableMultiSelect={false}
                                fieldOverrides = {fieldOverrides}
                                defaultNumRows={50}
                                onServerSideQuery={onServerSideQuery}
                                metadata={tableMetadata}
                                loadingState={loadingState}
                                filters={entityObjectMgr?.filters}
                                mgr={entityObjectMgr}
                                enableSidePanelEdit={true}
                            />}
           
        </>
    );
}

export default SearchEntityObject;



